import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ACTIVITY_CATEGORIES, formatActivityLocation } from '@yoyoclub/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../hooks/useAuth';
import { localize } from '../../i18n/script';
import { useSiteLanguage } from '../../i18n/useSiteLanguage';
import {
  newsletterApi,
  type Campaign,
  type CampaignContent,
  type CampaignStatus,
  type NewsletterActivity,
  type Segment,
} from '../../services/newsletterApi';

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

const READONLY_STATUSES: CampaignStatus[] = ['SENDING', 'SENT'];

// English category label for a course (matches the email card); null for OTHER.
function categoryLabel(code?: string | null): string | null {
  if (!code || code === 'OTHER') return null;
  return ACTIVITY_CATEGORIES.find((c) => c.value === code)?.labelEN ?? null;
}

// Short one-line summary shown under a picker/order item. Courses have no
// session or venue, so they show "Recorded course" + category + age range.
function itemSummary(activity: NewsletterActivity): string {
  if (activity.type === 'course') {
    return [
      'Recorded course',
      categoryLabel(activity.category),
      `Ages ${activity.ageMin}-${activity.ageMax}`,
    ]
      .filter(Boolean)
      .join(' · ');
  }
  const date = activity.nextSession
    ? new Date(activity.nextSession.startTime).toLocaleDateString()
    : 'No upcoming session';
  const location = formatActivityLocation({
    region: activity.region,
    locationDetails: activity.locationDetails,
    fallbackLocation: activity.location ?? undefined,
    locale: 'zh-CN',
    regionMode: 'smart',
  });
  return `${date} · ${location} · Ages ${activity.ageMin}-${activity.ageMax}`;
}

function CourseTag() {
  return (
    <span className="mr-1.5 inline-block rounded bg-purple-100 px-1.5 py-0.5 align-middle text-[10px] font-medium text-purple-700">
      Course
    </span>
  );
}

function statusBadge(status: CampaignStatus): { label: string; cls: string } {
  switch (status) {
    case 'SCHEDULED':
      return { label: 'Scheduled', cls: 'bg-blue-100 text-blue-700' };
    case 'SENDING':
      return { label: 'Sending', cls: 'bg-amber-100 text-amber-700' };
    case 'SENT':
      return { label: 'Sent', cls: 'bg-green-100 text-green-700' };
    case 'FAILED':
      return { label: 'Failed', cls: 'bg-red-100 text-red-700' };
    case 'CANCELLED':
      return { label: 'Cancelled', cls: 'bg-gray-100 text-gray-600' };
    default:
      return { label: 'Draft', cls: 'bg-gray-100 text-gray-600' };
  }
}

function SortableActivityItem({
  activity,
  blurb,
  disabled,
  onBlurbChange,
  onRemove,
}: {
  activity: NewsletterActivity;
  blurb: string;
  disabled: boolean;
  onBlurbChange: (id: string, value: string) => void;
  onRemove: (id: string) => void;
}) {
  const siteLanguage = useSiteLanguage();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: activity.id,
    disabled,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="border border-gray-200 rounded-lg p-3">
      <div className="flex items-center gap-3">
        <button
          {...attributes}
          {...listeners}
          disabled={disabled}
          className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 disabled:cursor-not-allowed"
          title="Drag to reorder"
          aria-label="Drag to reorder"
        >
          ⠿
        </button>
        {activity.coverImageUrl && (
          <img
            src={activity.coverImageUrl}
            alt=""
            className="w-10 h-10 rounded object-cover bg-gray-100"
          />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-800 truncate">
            {activity.type === 'course' && <CourseTag />}
            {localize(activity.titleCN, activity.titleEN, siteLanguage)}
          </p>
          <p className="text-xs text-gray-400 truncate">{itemSummary(activity)}</p>
        </div>
        <button
          onClick={() => onRemove(activity.id)}
          disabled={disabled}
          className="text-gray-300 hover:text-red-500 disabled:cursor-not-allowed"
          aria-label="Remove activity"
        >
          ✕
        </button>
      </div>
      <label className="block mt-2 text-xs text-gray-500">
        Intro line <span className="text-gray-300">(optional)</span>
      </label>
      <input
        value={blurb}
        disabled={disabled}
        onChange={(e) => onBlurbChange(activity.id, e.target.value)}
        placeholder="One-line intro..."
        className="mt-1 w-full px-3 py-1.5 border border-gray-200 rounded-md text-xs disabled:bg-gray-50"
      />
    </div>
  );
}

export default function AdminNewsletter() {
  const siteLanguage = useSiteLanguage();
  const navigate = useNavigate();
  const { isAuthenticated, getToken, loading: authLoading } = useAuth();

  const [activities, setActivities] = useState<NewsletterActivity[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // Campaign identity + status
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [status, setStatus] = useState<CampaignStatus>('DRAFT');

  // Editable fields
  const [subject, setSubject] = useState('');
  const [previewText, setPreviewText] = useState('');
  const [segmentId, setSegmentId] = useState('');
  const [showImage, setShowImage] = useState(false);
  const [showTitle, setShowTitle] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [heroTitle, setHeroTitle] = useState('Spring Fun Festival');
  const [heroDateRange, setHeroDateRange] = useState('');
  const [heroImageUrl, setHeroImageUrl] = useState('');
  const [welcomeHeading, setWelcomeHeading] = useState('Dear YOYO Club Families!');
  const [welcomeBody, setWelcomeBody] = useState('');
  const [note, setNote] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [blurbs, setBlurbs] = useState<Record<string, string>>({});

  // Send controls
  const [sendMode, setSendMode] = useState<'now' | 'schedule'>('now');
  const [scheduledAt, setScheduledAt] = useState('');
  const [testRecipients, setTestRecipients] = useState('dev@sayyoyo.com');

  // UI state
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testState, setTestState] = useState<string | null>(null);

  const readOnly = READONLY_STATUSES.includes(status) || status === 'SCHEDULED';

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const selectedActivities = useMemo(
    () =>
      selectedIds
        .map((id) => activities.find((a) => a.id === id))
        .filter((a): a is NewsletterActivity => a != null),
    [selectedIds, activities]
  );

  const content: CampaignContent = useMemo(
    () => ({
      hero: {
        showImage,
        showTitle,
        showWelcome,
        title: heroTitle,
        dateRange: heroDateRange,
        imageUrl: heroImageUrl,
      },
      welcome: showWelcome ? { heading: welcomeHeading, body: welcomeBody } : undefined,
      note: note || undefined,
      activities: selectedIds.map((id) => ({ activityId: id, blurb: blurbs[id] || undefined })),
    }),
    [
      showImage,
      showTitle,
      showWelcome,
      heroTitle,
      heroDateRange,
      heroImageUrl,
      welcomeHeading,
      welcomeBody,
      note,
      selectedIds,
      blurbs,
    ]
  );

  function applyCampaign(c: Campaign) {
    setCampaignId(c.id);
    setStatus(c.status);
    setSubject(c.subject);
    setPreviewText(c.previewText ?? '');
    setSegmentId(c.segmentId ?? '');
    setScheduledAt(c.scheduledAt ? c.scheduledAt.slice(0, 16) : '');
    const ct = c.content || ({} as CampaignContent);
    setShowImage(!!ct.hero?.showImage);
    setShowTitle(!!ct.hero?.showTitle);
    setShowWelcome(!!ct.hero?.showWelcome);
    setHeroTitle(ct.hero?.title || 'Spring Fun Festival');
    setHeroDateRange(ct.hero?.dateRange || '');
    setHeroImageUrl(ct.hero?.imageUrl || '');
    setWelcomeHeading(ct.welcome?.heading || 'Dear YOYO Club Families!');
    setWelcomeBody(ct.welcome?.body || '');
    setNote(ct.note || '');
    setSelectedIds((ct.activities || []).map((a) => a.activityId));
    setBlurbs(Object.fromEntries((ct.activities || []).map((a) => [a.activityId, a.blurb || ''])));
  }

  // Initial load: activities, segments, and a draft campaign (reuse latest draft).
  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/login');
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const token = await getToken();
        if (!token) return;
        const [acts, segs, campaigns] = await Promise.all([
          newsletterApi.getActivities(token),
          newsletterApi.getSegments(token).catch(() => [] as Segment[]),
          newsletterApi.listCampaigns(token),
        ]);
        if (cancelled) return;
        setActivities(acts);
        setSegments(segs);
        const draft = campaigns.find((c) => c.status === 'DRAFT');
        if (draft) {
          applyCampaign(draft);
        } else {
          const created = await newsletterApi.createCampaign(token, {
            subject: '',
            content,
          });
          if (!cancelled) applyCampaign(created);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Autosave + preview. Saves are serialized (each waits for the previous to
  // settle) and seq-guarded, so a slower older response can neither overwrite a
  // newer draft on the server nor replace the preview with stale HTML.
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveSeqRef = useRef(0);
  const inFlightRef = useRef<Promise<void>>(Promise.resolve());

  const payload = useMemo(
    () => ({
      subject,
      previewText: previewText || undefined,
      segmentId: segmentId || undefined,
      content,
    }),
    [subject, previewText, segmentId, content]
  );
  const payloadRef = useRef(payload);
  payloadRef.current = payload;

  const persist = useCallback(async () => {
    if (!campaignId) return;
    const seq = ++saveSeqRef.current;
    setSaveState('saving');
    const run = (async () => {
      await inFlightRef.current.catch(() => {});
      try {
        const token = await getToken();
        if (!token) return;
        await newsletterApi.updateCampaign(token, campaignId, payloadRef.current);
        const { html } = await newsletterApi.preview(token, campaignId);
        if (seq !== saveSeqRef.current) return; // a newer edit superseded this
        setPreviewHtml(html);
        setSaveState('saved');
      } catch (err) {
        if (seq !== saveSeqRef.current) return;
        setSaveState('error');
        setError(err instanceof Error ? err.message : 'Autosave failed');
      }
    })();
    inFlightRef.current = run;
    return run;
  }, [campaignId, getToken]);

  // Flush any pending autosave and wait for it, so a send never broadcasts a
  // stale snapshot.
  const flushSave = useCallback(async () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    await persist();
  }, [persist]);

  const serialized = JSON.stringify(payload);
  useEffect(() => {
    if (!campaignId || loading || readOnly) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaveState('saving');
    saveTimer.current = setTimeout(() => {
      void persist();
    }, 700);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized, campaignId, loading, readOnly]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setSelectedIds((prev) => {
        const oldIndex = prev.indexOf(active.id as string);
        const newIndex = prev.indexOf(over.id as string);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  }, []);

  const removeActivity = useCallback((id: string) => {
    setSelectedIds((prev) => prev.filter((x) => x !== id));
  }, []);

  const toggleActivity = useCallback((id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  // Operates only on activities visible in the current list. A draft may carry
  // stale ids (sessions that expired since it was saved); those must not affect
  // the count or flip Select all into clearing everything.
  const selectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const visibleIds = activities.map((a) => a.id);
      const allVisibleSelected =
        visibleIds.length > 0 && visibleIds.every((id) => prev.includes(id));
      if (allVisibleSelected) return prev.filter((id) => !visibleIds.includes(id));
      const toAdd = visibleIds.filter((id) => !prev.includes(id));
      return [...prev, ...toAdd];
    });
  }, [activities]);

  const setBlurb = useCallback((id: string, value: string) => {
    setBlurbs((prev) => ({ ...prev, [id]: value }));
  }, []);

  const segmentName = segments.find((s) => s.id === segmentId)?.name || '(none selected)';

  // Scheduling requires a future date/time; otherwise the empty value would fall
  // through to an immediate send.
  const scheduleValid =
    sendMode === 'now' || (!!scheduledAt && new Date(scheduledAt).getTime() > Date.now());
  const canSend = !!subject.trim() && !!segmentId && selectedActivities.length > 0 && scheduleValid;

  async function handleSendTest() {
    const recipients = testRecipients
      .split(/[,\s]+/)
      .map((r) => r.trim())
      .filter(Boolean);
    if (!campaignId || recipients.length === 0) return;
    setBusy(true);
    setTestState(null);
    try {
      const token = await getToken();
      if (!token) return;
      await newsletterApi.test(token, campaignId, recipients);
      setTestState(`Test sent to ${recipients.join(', ')}`);
    } catch (err) {
      setTestState(err instanceof Error ? err.message : 'Test send failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleSend() {
    if (!campaignId) return;
    setBusy(true);
    setError(null);
    try {
      const token = await getToken();
      if (!token) return;
      const when =
        sendMode === 'schedule' && scheduledAt ? new Date(scheduledAt).toISOString() : null;
      if (sendMode === 'schedule' && !when) {
        setConfirmOpen(false);
        setError('Choose a future date and time to schedule.');
        return;
      }
      // Persist the latest edits before sending so we never broadcast a stale snapshot.
      await flushSave();
      const result = await newsletterApi.send(token, campaignId, when);
      setStatus(result.status);
      setConfirmOpen(false);
      setInfo(result.status === 'SCHEDULED' ? 'Broadcast scheduled.' : 'Broadcast sent.');
    } catch (err) {
      setConfirmOpen(false);
      setError(err instanceof Error ? err.message : 'Send failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleCancel() {
    if (!campaignId) return;
    setBusy(true);
    try {
      const token = await getToken();
      if (!token) return;
      const c = await newsletterApi.cancel(token, campaignId);
      applyCampaign(c);
      setInfo('Scheduled send cancelled. Campaign is editable again.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Cancel failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleClone() {
    if (!campaignId) return;
    setBusy(true);
    try {
      const token = await getToken();
      if (!token) return;
      const c = await newsletterApi.cloneCampaign(token, campaignId);
      applyCampaign(c);
      setInfo('Cloned into a new editable draft.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Clone failed');
    } finally {
      setBusy(false);
    }
  }

  const badge = statusBadge(status);
  // Only surface autosave activity and failures. The steady "saved" state is
  // intentionally silent: autosave is implicit, so a persistent "Saved" label is
  // just noise. A failure, however, must be loud (see the red styling below).
  const saveLabel =
    saveState === 'saving' ? 'Saving...' : saveState === 'error' ? 'Save failed' : '';

  return (
    <AdminLayout title="Newsletter">
      <div className="space-y-4 pb-28">
        {/* Top bar */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900">Newsletter</h2>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.cls}`}>
              {badge.label}
            </span>
            {saveLabel && (
              <span
                className={`text-xs ${
                  saveState === 'error' ? 'font-medium text-red-600' : 'text-gray-400'
                }`}
              >
                {saveLabel}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {(status === 'SENT' || status === 'CANCELLED' || status === 'FAILED') && (
              <button
                onClick={handleClone}
                disabled={busy}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Clone to new draft
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-red-500">
              ×
            </button>
          </div>
        )}
        {info && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded flex justify-between">
            <span>{info}</span>
            <button onClick={() => setInfo(null)} className="text-green-600">
              ×
            </button>
          </div>
        )}

        {loading ? (
          <div className="text-gray-500 py-12 text-center">Loading...</div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,680px)] gap-6">
            {/* LEFT: compose */}
            <div className="space-y-4 min-w-0">
              {/* Campaign */}
              <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                <h3 className="font-semibold text-gray-800 mb-4">Campaign</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                    <input
                      value={subject}
                      disabled={readOnly}
                      onChange={(e) => setSubject(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm disabled:bg-gray-50"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Preview text{' '}
                      <span className="text-gray-400 font-normal">(inbox preheader)</span>
                    </label>
                    <input
                      value={previewText}
                      disabled={readOnly}
                      onChange={(e) => setPreviewText(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm disabled:bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Segment</label>
                    <select
                      value={segmentId}
                      disabled={readOnly}
                      onChange={(e) => setSegmentId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white disabled:bg-gray-50"
                    >
                      <option value="">Select audience...</option>
                      {segments.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Send time
                    </label>
                    <div className="flex items-center gap-4 text-sm h-[38px]">
                      <label className="flex items-center gap-1.5">
                        <input
                          type="radio"
                          checked={sendMode === 'now'}
                          disabled={readOnly}
                          onChange={() => setSendMode('now')}
                        />{' '}
                        Now
                      </label>
                      <label className="flex items-center gap-1.5">
                        <input
                          type="radio"
                          checked={sendMode === 'schedule'}
                          disabled={readOnly}
                          onChange={() => setSendMode('schedule')}
                        />{' '}
                        Schedule
                      </label>
                    </div>
                    {sendMode === 'schedule' && (
                      <input
                        type="datetime-local"
                        value={scheduledAt}
                        disabled={readOnly}
                        onChange={(e) => setScheduledAt(e.target.value)}
                        className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md text-sm disabled:bg-gray-50"
                      />
                    )}
                  </div>
                </div>
              </section>

              {/* Header */}
              <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                <h3 className="font-semibold text-gray-800 mb-4">Header</h3>
                <div className="space-y-4 text-sm">
                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={showImage}
                      disabled={readOnly}
                      onChange={(e) => setShowImage(e.target.checked)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <span className="text-gray-700 font-medium">Hero image</span>
                      {showImage && (
                        <input
                          value={heroImageUrl}
                          disabled={readOnly}
                          onChange={(e) => setHeroImageUrl(e.target.value)}
                          placeholder="https://cdn.sayyoyo.com/..."
                          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        />
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={showTitle}
                      disabled={readOnly}
                      onChange={(e) => setShowTitle(e.target.checked)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <span className="text-gray-700 font-medium">Title banner</span>
                      {showTitle && (
                        <div className="mt-1 grid grid-cols-2 gap-2">
                          <input
                            value={heroTitle}
                            disabled={readOnly}
                            onChange={(e) => setHeroTitle(e.target.value)}
                            placeholder="Title"
                            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                          <input
                            value={heroDateRange}
                            disabled={readOnly}
                            onChange={(e) => setHeroDateRange(e.target.value)}
                            placeholder="Date range"
                            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={showWelcome}
                      disabled={readOnly}
                      onChange={(e) => setShowWelcome(e.target.checked)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <span className="text-gray-700 font-medium">Welcome message</span>
                      {showWelcome && (
                        <div className="mt-1 space-y-2">
                          <input
                            value={welcomeHeading}
                            disabled={readOnly}
                            onChange={(e) => setWelcomeHeading(e.target.value)}
                            placeholder="Welcome heading"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                          <textarea
                            value={welcomeBody}
                            disabled={readOnly}
                            onChange={(e) => setWelcomeBody(e.target.value)}
                            placeholder="Welcome body"
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </section>

              {/* Activities */}
              <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-800">Activities &amp; courses</h3>
                  <span className="text-xs text-gray-400">
                    {selectedActivities.length} of {activities.length} selected
                  </span>
                </div>

                {/* Pick from the upcoming activities */}
                <div className="flex items-center justify-between mb-2">
                  <button
                    onClick={selectAll}
                    disabled={readOnly || activities.length === 0}
                    className="text-sm text-green-700 hover:text-green-900 disabled:text-gray-300"
                  >
                    {selectedActivities.length === activities.length && activities.length > 0
                      ? 'Deselect all'
                      : 'Select all'}
                  </button>
                </div>
                {activities.length === 0 ? (
                  <p className="text-sm text-gray-400 py-4 text-center">
                    No activities or courses found.
                  </p>
                ) : (
                  <div className="space-y-2 max-h-[340px] overflow-y-auto pr-1">
                    {activities.map((activity) => {
                      const isSelected = selectedIds.includes(activity.id);
                      return (
                        <label
                          key={activity.id}
                          className={`flex items-start gap-3 p-2.5 rounded-lg border transition-colors ${
                            isSelected
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 hover:bg-gray-50'
                          } ${readOnly ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={readOnly}
                            onChange={() => toggleActivity(activity.id)}
                            className="mt-1 h-4 w-4 text-green-600 rounded"
                          />
                          {activity.coverImageUrl && (
                            <img
                              src={activity.coverImageUrl}
                              alt=""
                              className="w-12 h-12 rounded object-cover flex-shrink-0 bg-gray-100"
                            />
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-sm text-gray-900 truncate">
                              {activity.type === 'course' && <CourseTag />}
                              {localize(activity.titleCN, activity.titleEN, siteLanguage)}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {itemSummary(activity)}
                            </p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}

                {/* Order + per-activity intro (drag to reorder) */}
                {selectedActivities.length > 0 && (
                  <div className="pt-3 mt-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Order &amp; intro <span className="text-gray-400">(drag to reorder)</span>
                    </p>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext items={selectedIds} strategy={verticalListSortingStrategy}>
                        <div className="space-y-2">
                          {selectedActivities.map((a) => (
                            <SortableActivityItem
                              key={a.id}
                              activity={a}
                              blurb={blurbs[a.id] || ''}
                              disabled={readOnly}
                              onBlurbChange={setBlurb}
                              onRemove={removeActivity}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  </div>
                )}
              </section>

              {/* Message */}
              <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                <h3 className="font-semibold text-gray-800 mb-4">Message</h3>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  A Note from YOYO Club{' '}
                  <span className="text-gray-400 font-normal">
                    (optional; default used if empty)
                  </span>
                </label>
                <textarea
                  value={note}
                  disabled={readOnly}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm disabled:bg-gray-50"
                />
              </section>
            </div>

            {/* RIGHT: preview */}
            <div className="min-w-0">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 sticky top-4">
                <div className="flex items-center justify-between px-5 py-3">
                  <h3 className="font-semibold text-gray-800">Preview</h3>
                  <div className="inline-flex rounded-md border border-gray-300 overflow-hidden text-sm">
                    <button
                      onClick={() => setPreviewMode('desktop')}
                      className={`px-3 py-1 ${previewMode === 'desktop' ? 'bg-green-600 text-white' : 'text-gray-600'}`}
                    >
                      Desktop
                    </button>
                    <button
                      onClick={() => setPreviewMode('mobile')}
                      className={`px-3 py-1 ${previewMode === 'mobile' ? 'bg-green-600 text-white' : 'text-gray-600'}`}
                    >
                      Mobile
                    </button>
                  </div>
                </div>
                <div className="px-5 py-2.5 bg-gray-50 text-xs text-gray-500 space-y-0.5">
                  <p>
                    <span className="text-gray-400">Subject</span> {subject || '(empty)'}
                  </p>
                  <p>
                    <span className="text-gray-400">Preview</span> {previewText || '(empty)'}
                  </p>
                </div>
                <div className="p-4 bg-gray-100 flex justify-center">
                  <iframe
                    title="Email preview"
                    srcDoc={previewHtml}
                    className="bg-white border-0"
                    style={{
                      width: previewMode === 'mobile' ? 390 : '100%',
                      height: 640,
                      maxWidth: '100%',
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sticky action bar */}
      {!loading && (
        <div className="fixed bottom-0 left-0 right-0 bg-white shadow-[0_-4px_12px_rgba(0,0,0,0.06)] px-6 py-3 flex items-center justify-between gap-4 flex-wrap z-30">
          <div className="flex items-center gap-2 flex-wrap">
            <input
              value={testRecipients}
              onChange={(e) => setTestRecipients(e.target.value)}
              placeholder="test@example.com"
              className="px-2 py-1.5 border border-gray-300 rounded-md text-sm w-56"
            />
            <button
              onClick={handleSendTest}
              disabled={busy || !campaignId}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Send test
            </button>
            {testState && <span className="text-xs text-gray-500">{testState}</span>}
          </div>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-green-50 text-green-800 text-sm font-medium border border-green-200">
              {segmentName}
              {sendMode === 'schedule' && scheduledAt
                ? ` · ${new Date(scheduledAt).toLocaleString()}`
                : ' · Now'}
            </span>
            {status === 'SCHEDULED' ? (
              <button
                onClick={handleCancel}
                disabled={busy}
                className="px-5 py-2 bg-red-600 text-white text-sm font-semibold rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                Cancel schedule
              </button>
            ) : (
              <button
                onClick={() => setConfirmOpen(true)}
                disabled={busy || readOnly || !canSend}
                className="px-5 py-2 bg-green-600 text-white text-sm font-semibold rounded-md hover:bg-green-700 disabled:opacity-50"
                title={!canSend ? 'Set a subject, segment, and at least one activity' : ''}
              >
                {sendMode === 'schedule' ? 'Schedule send' : 'Send now'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Confirm modal */}
      {confirmOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {sendMode === 'schedule' ? 'Schedule this broadcast?' : 'Send this broadcast now?'}
              </h3>
            </div>
            <div className="px-6 py-4 space-y-3 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-gray-500">Subject</span>
                <span className="text-gray-900 font-medium text-right truncate">{subject}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Audience</span>
                <span className="text-gray-900 font-semibold">{segmentName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">When</span>
                <span className="text-gray-900 font-medium">
                  {sendMode === 'schedule' && scheduledAt
                    ? new Date(scheduledAt).toLocaleString()
                    : 'Immediately'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Activities</span>
                <span className="text-gray-900">{selectedActivities.length}</span>
              </div>
              <div className="mt-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2">
                Content is frozen at send. Editing later cancels this and creates a new broadcast.
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 flex justify-end gap-2">
              <button
                onClick={() => setConfirmOpen(false)}
                disabled={busy}
                className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={busy}
                className="px-4 py-2 text-sm bg-green-600 text-white font-semibold rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                {busy
                  ? 'Working...'
                  : sendMode === 'schedule'
                    ? 'Schedule Broadcast'
                    : 'Send Broadcast Now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
